/* eslint-disable no-plusplus */
/* eslint-disable camelcase */
/* eslint-disable react/display-name */
/**
 * @file PsychosocialRiskView.jsx
 * @author Fernando Ruano
 * @description Psychosocial Risk View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import moment from 'moment';

// Components
import DashboardCard from 'components/DashboardCard';
import DashboardDoughnut from 'components/Charts/DashboardDoughnut';

// Commons
import styleGuideColors from 'commons/styleGuideColors';
import { getMonthShortName } from 'commons/helpers/utils';
import useTruncateAndFormat from 'hook/useTruncateAndFormat';

/**
 * Psychosocial Risk View
 * @param { Object } dashboardStaffActions - DashboardStaff Actions.
 * @return { Node } React node.
 */

function PsychosocialRiskPsycologyView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const [chartData, setChartData] = useState(null);
  const [current, setCurrent] = useState(moment().format('YYYY-MM-01'));

  const { getPsychosocialRiskPsychology } = dashboardStaffActions;

  const getMonthArray = () => {
    const lastSixMonths = [];

    for (let index = 0; index < 6; index++) {
      const currentMonth = moment().subtract(
        index,
        index > 1 ? 'months' : 'month'
      );
      const shortMonth = getMonthShortName(Number(currentMonth.month()));
      const translatedMonth = translate(shortMonth);
      lastSixMonths.push({
        value: translatedMonth,
        key: currentMonth.format('YYYY-MM-01')
      });
    }

    return {
      label: translate('Mes'),
      items: lastSixMonths
    };
  };

  const getCurrentMonth = () => {
    const [currentMonth] = getMonthArray().items.filter(
      (itm) => itm.key === current
    );
    return currentMonth;
  };

  const onGetData = (_, resp) => {
    let data = [];
    const classifications = [
      {
        label: 'Bajo',
        classification: 'Psicología: Riesgo bajo',
        color: styleGuideColors.baxterBrandTeal
      },
      {
        label: 'Medio',
        classification: 'Psicología: Riesgo medio',
        color: styleGuideColors.graphYellow
      },
      {
        label: 'Alto',
        classification: 'Psicología: Riesgo alto',
        color: styleGuideColors.alertError
      },
      {
        label: 'Sin Registro',
        classification: 'Sin Registro',
        color: styleGuideColors.graphGreyStrokes
      }
    ];

    let total = 0;

    if (resp?.data) {
      resp.data.forEach(({ cantidad }) => {
        total += cantidad;
      });
      classifications.forEach(({ label, color, classification }) => {
        const respElement = resp.data.find(
          ({ clasificacion }) => clasificacion === classification
        );
        if (respElement) {
          const value = (respElement.cantidad * 100) / total;
          data = [
            ...data,
            {
              value: useTruncateAndFormat(value, 2),
              label,
              classification,
              color,
              cantidad: respElement.cantidad
            }
          ];
        }
      });
      setChartData(data);
    }
  };

  const getIndicatorInfo = (item) => {
    setChartData(null);
    getPsychosocialRiskPsychology({ clinicId, month: item.key }, onGetData);
  };

  useEffect(() => {
    getIndicatorInfo(getCurrentMonth());
  }, [current, translate, clinicId]);

  const handleDetailClick = (index) => {
    const { classification: riskParam, cantidad: totalItems } =
      chartData[index];
    history.push(
      `/main/dashboard-detail/psychosocial-risk-psycology/${riskParam}/${
        getCurrentMonth().key
      }/${totalItems}`
    );
  };

  return (
    <DashboardCard id="F34" loading={!chartData}>
      <DashboardDoughnut
        title={translate('dashboardStaff.psychosocialRiskPsychology')}
        dataValues={chartData || []}
        onDetailClick={handleDetailClick}
        selectMenu={{
          menu: getMonthArray(),
          onSelectItem: (item) => {
            setCurrent(item.key);
          },
          current: getCurrentMonth().value
        }}
      />
    </DashboardCard>
  );
}

/**
 * Component definition of proptypes.
 */
PsychosocialRiskPsycologyView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getPsychosocialRiskPsychology: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default PsychosocialRiskPsycologyView;
